/**
 * Live "Expires in Xh" indicator with a pulsing accent dot (spec.md § Top bar,
 * § Expiry & lifecycle). Pure data — derived from the pad's `expiresAt`, so the
 * shell owns it without coupling to the editor.
 *
 * - Shows hours when ≥ 1h remain, otherwise minutes; "Expired" once past.
 * - Ambers (danger tint) when under 3 hours remain.
 * - The dot pulses on a ~2.6s loop; the pulse is disabled under
 *   `prefers-reduced-motion` (handled by the global reduced-motion rule in
 *   styles/index.css, which clamps animation duration).
 */

import { useEffect, useState } from "react";

const HOUR_MS = 60 * 60 * 1000;
const MINUTE_MS = 60 * 1000;
const AMBER_THRESHOLD_MS = 3 * HOUR_MS;

function formatRemaining(expiresAtMs: number, nowMs: number): string {
  const remaining = expiresAtMs - nowMs;
  if (remaining <= 0) {
    return "Expired";
  }
  if (remaining >= HOUR_MS) {
    return `Expires in ${String(Math.floor(remaining / HOUR_MS))}h`;
  }
  const minutes = Math.max(1, Math.floor(remaining / MINUTE_MS));
  return `Expires in ${String(minutes)}m`;
}

export function ExpiryIndicator({ expiresAt }: { expiresAt: string }): React.JSX.Element {
  const expiresAtMs = new Date(expiresAt).getTime();
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    // Re-render once a minute so the countdown stays roughly live without churn.
    const id = setInterval(() => {
      setNowMs(Date.now());
    }, MINUTE_MS);
    return () => {
      clearInterval(id);
    };
  }, []);

  const remaining = expiresAtMs - nowMs;
  const ambering = remaining > 0 && remaining < AMBER_THRESHOLD_MS;
  const label = formatRemaining(expiresAtMs, nowMs);

  return (
    <span
      className={`flex items-center gap-2 font-mono text-[12.5px] ${
        ambering ? "text-danger" : "text-text-2"
      }`}
      // Polite live region: announces the countdown without interrupting.
      aria-live="polite"
    >
      <span
        aria-hidden="true"
        className={`size-1.5 rounded-full motion-safe:animate-pulse ${
          ambering ? "bg-danger" : "bg-accent"
        }`}
      />
      {label}
    </span>
  );
}
